import React, { useMemo, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core'
import { ArrowBack as ArrowBackIcon } from '@material-ui/icons'
import {
    HEALTH_STATUS_BUCKETS,
    MetricDefinition,
    PROPERTY_METRICS,
    PROPERTY_METRIC_GROUPS,
    PROPERTY_KPI_FILTER_LABELS,
    PropertySnapshotDetail,
    PropertySnapshotPropertyRow,
    formatMetricValue,
    isPropertyKpiFilterable,
    propertyMatchesKpiFilter,
    resolvePropertyHealthCategory,
} from './auditMetrics'

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
        width: '100%',
    },
    toolbar: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    toolbarLeft: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        minWidth: 0,
    },
    backButton: {
        textTransform: 'none',
        paddingLeft: 0,
        minWidth: 0,
        color: theme.palette.text.secondary,
        '&:hover': {
            backgroundColor: 'transparent',
            color: theme.palette.primary.main,
        },
    },
    title: {
        fontWeight: 600,
    },
    subtitle: {
        color: theme.palette.text.secondary,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
    },
    groupsStack: {
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
    },
    groupStrip: {
        display: 'flex',
        alignItems: 'stretch',
        gap: 0,
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
        },
    },
    groupLabelRail: {
        flex: '0 0 148px',
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1.25, 1.5),
        backgroundColor: theme.palette.grey[50],
        borderRight: `2px solid ${theme.palette.grey[700]}`,
        [theme.breakpoints.down('xs')]: {
            flex: 'none',
            borderRight: 'none',
            borderBottom: `2px solid ${theme.palette.grey[700]}`,
            paddingBottom: theme.spacing(0.75),
        },
    },
    groupTitle: {
        fontWeight: 700,
        fontSize: '0.7rem',
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        color: theme.palette.text.secondary,
        lineHeight: 1.3,
    },
    metricStrip: {
        flex: 1,
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'stretch',
        minWidth: 0,
        overflowX: 'auto',
    },
    metricCell: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 4,
        padding: theme.spacing(1.25, 1.5),
        width: 128,
        minWidth: 128,
        maxWidth: 128,
        boxSizing: 'border-box',
        borderRight: `1px solid ${theme.palette.divider}`,
    },
    metricCellClickable: {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    metricCellActive: {
        backgroundColor: theme.palette.action.selected,
        boxShadow: `inset 0 -3px 0 ${theme.palette.primary.main}`,
    },
    kpiLabel: {
        fontSize: '0.65rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.25,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        wordBreak: 'break-word',
        minHeight: '1.625rem',
    },
    kpiValue: {
        fontWeight: 700,
        fontSize: '1.05rem',
        lineHeight: 1.15,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    warnValue: {
        color: theme.palette.warning.dark,
    },
    empty: {
        padding: theme.spacing(4),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
        marginBottom: theme.spacing(1),
    },
    sectionTitle: {
        fontWeight: 600,
    },
    sectionHint: {
        color: theme.palette.text.secondary,
    },
    filterChip: {
        marginLeft: 'auto',
    },
    tableWrap: {
        width: '100%',
        overflowX: 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
    },
    propertyName: {
        fontWeight: 500,
        whiteSpace: 'nowrap',
    },
    healthSwatch: {
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: 2,
        marginRight: 6,
        verticalAlign: 'middle',
    },
    linkButton: {
        textTransform: 'none',
        padding: 0,
        minWidth: 0,
    },
}))

interface Props {
    onBack: () => void
    reportLabel?: string | null
    metrics?: Record<string, number | null> | null
    detail?: PropertySnapshotDetail | null
    isLoading?: boolean
    errorMessage?: string | null
    onViewReport?: (xlsxUrl: string) => void
}

const metricByKey = PROPERTY_METRICS.reduce<Record<string, MetricDefinition>>(
    (acc, metric) => {
        acc[metric.key] = metric
        return acc
    },
    {},
)

for (const bucket of HEALTH_STATUS_BUCKETS) {
    metricByKey[bucket.key] = {
        key: bucket.key,
        label: bucket.label,
        format: 'number',
    }
}

const healthColorByKey = HEALTH_STATUS_BUCKETS.reduce<Record<string, string>>(
    (acc, bucket) => {
        acc[bucket.key] = bucket.color
        return acc
    },
    {},
)

const healthColorByCategory = HEALTH_STATUS_BUCKETS.reduce<
    Record<string, string>
>((acc, bucket) => {
    acc[bucket.label] = bucket.color
    return acc
}, {})

const GAP_METRIC_KEYS = new Set([
    'properties_with_0_vendors',
    'properties_with_0_damage_types',
    'properties_that_havent_synced_leases',
    'properties_with_no_active_schedule',
])

const getMetricHint = (
    key: string,
    metrics: Record<string, number | null | undefined>,
    filterable: boolean,
): string | null => {
    const filterHint = filterable ? 'Click to filter the property list' : null
    let detailHint: string | null = null
    switch (key) {
        case 'avg_health_score':
            detailHint = 'Average apartment health score across the portfolio'
            break
        case 'health_critical':
        case 'health_poor':
        case 'health_fair':
        case 'health_good':
        case 'health_excellent': {
            const total = metrics.total_apartments
            if (total != null) {
                detailHint = `of ${formatMetricValue(
                    total,
                    'number',
                )} apartments`
            }
            break
        }
        case 'active_schedule_pct': {
            const total = metrics.total_apartments
            const noSchedule = metrics.properties_with_no_active_schedule
            if (total != null && noSchedule != null) {
                const withSchedule = Math.max(0, total - noSchedule)
                detailHint = `${formatMetricValue(
                    withSchedule,
                    'number',
                )} / ${formatMetricValue(
                    total,
                    'number',
                )} properties with an active schedule`
            }
            break
        }
        default:
            break
    }
    if (filterHint && detailHint) {
        return `${detailHint}. ${filterHint}`
    }
    return filterHint || detailHint
}

const getValueClass = (
    key: string,
    value: number | null | undefined,
    classes: ReturnType<typeof useStyles>,
): string => {
    if (value == null || value === 0) {
        return classes.kpiValue
    }
    if (GAP_METRIC_KEYS.has(key)) {
        return `${classes.kpiValue} ${classes.warnValue}`
    }
    return classes.kpiValue
}

const getValueStyle = (key: string): React.CSSProperties | undefined => {
    const color = healthColorByKey[key]
    return color ? { color } : undefined
}

export const PropertySnapshotDetailView: React.FC<Props> = ({
    onBack,
    reportLabel = null,
    metrics: rowMetrics = null,
    detail = null,
    isLoading = false,
    errorMessage = null,
    onViewReport,
}) => {
    const classes = useStyles()
    const [activeFilter, setActiveFilter] = useState<string | null>(null)

    const metrics = useMemo(
        () => (rowMetrics ?? {}) as Record<string, number | null | undefined>,
        [rowMetrics],
    )

    const properties = detail?.detail?.properties ?? []
    const hasMetrics = Object.keys(metrics).length > 0

    const filteredProperties = useMemo(
        () =>
            properties.filter((property) =>
                propertyMatchesKpiFilter(property, activeFilter),
            ),
        [properties, activeFilter],
    )

    const handleMetricClick = (key: string) => {
        if (!isPropertyKpiFilterable(key)) {
            return
        }
        setActiveFilter((prev) => (prev === key ? null : key))
    }

    const filterLabel = activeFilter
        ? PROPERTY_KPI_FILTER_LABELS[activeFilter] || activeFilter
        : null

    return (
        <Box className={classes.root}>
            <Box className={classes.toolbar}>
                <Box className={classes.toolbarLeft}>
                    <Button
                        className={classes.backButton}
                        startIcon={<ArrowBackIcon />}
                        onClick={onBack}
                        size="small"
                    >
                        Back to table
                    </Button>
                    <Box>
                        <Typography variant="h6" className={classes.title}>
                            Property Audit
                            {reportLabel ? ` · ${reportLabel}` : ''}
                        </Typography>
                        <Typography
                            variant="body2"
                            className={classes.subtitle}
                        >
                            Snapshot detail for this report period. Click a KPI
                            to filter the property list. Property filters above
                            still apply.
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box className={classes.content}>
                {!hasMetrics ? (
                    <Typography className={classes.empty}>
                        No metrics available for this snapshot.
                    </Typography>
                ) : (
                    <Box className={classes.groupsStack}>
                        {PROPERTY_METRIC_GROUPS.map((group) => (
                            <Box key={group.id} className={classes.groupStrip}>
                                <Box className={classes.groupLabelRail}>
                                    <Typography className={classes.groupTitle}>
                                        {group.label}
                                    </Typography>
                                </Box>
                                <Box className={classes.metricStrip}>
                                    {group.metricKeys.map((key) => {
                                        const definition = metricByKey[key]
                                        if (!definition) {
                                            return null
                                        }
                                        const value = metrics[key]
                                        const filterable = isPropertyKpiFilterable(
                                            key,
                                        )
                                        const hint = getMetricHint(
                                            key,
                                            metrics,
                                            filterable,
                                        )
                                        const isActive = activeFilter === key
                                        return (
                                            <Box
                                                key={key}
                                                className={`${
                                                    classes.metricCell
                                                }${
                                                    filterable
                                                        ? ` ${classes.metricCellClickable}`
                                                        : ''
                                                }${
                                                    isActive
                                                        ? ` ${classes.metricCellActive}`
                                                        : ''
                                                }`}
                                                title={hint || undefined}
                                                onClick={
                                                    filterable
                                                        ? () =>
                                                              handleMetricClick(
                                                                  key,
                                                              )
                                                        : undefined
                                                }
                                                role={
                                                    filterable
                                                        ? 'button'
                                                        : undefined
                                                }
                                                tabIndex={
                                                    filterable ? 0 : undefined
                                                }
                                                onKeyDown={
                                                    filterable
                                                        ? (e) => {
                                                              if (
                                                                  e.key ===
                                                                      'Enter' ||
                                                                  e.key === ' '
                                                              ) {
                                                                  e.preventDefault()
                                                                  handleMetricClick(
                                                                      key,
                                                                  )
                                                              }
                                                          }
                                                        : undefined
                                                }
                                            >
                                                <Typography
                                                    className={classes.kpiLabel}
                                                >
                                                    {definition.label}
                                                </Typography>
                                                <Typography
                                                    className={getValueClass(
                                                        key,
                                                        value,
                                                        classes,
                                                    )}
                                                    style={getValueStyle(key)}
                                                >
                                                    {formatMetricValue(
                                                        value,
                                                        definition.format,
                                                    )}
                                                </Typography>
                                            </Box>
                                        )
                                    })}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}

                <Box>
                    <Box className={classes.sectionHeader}>
                        <Box>
                            <Typography className={classes.sectionTitle}>
                                Properties
                            </Typography>
                            <Typography
                                variant="body2"
                                className={classes.sectionHint}
                            >
                                {isLoading && !detail
                                    ? 'Loading properties…'
                                    : `${filteredProperties.length.toLocaleString()} of ${properties.length.toLocaleString()} properties`}
                            </Typography>
                        </Box>
                        {filterLabel && (
                            <Chip
                                className={classes.filterChip}
                                size="small"
                                color="primary"
                                label={`Filter: ${filterLabel}`}
                                onDelete={() => setActiveFilter(null)}
                            />
                        )}
                    </Box>

                    {isLoading && !detail ? (
                        <Box className={classes.empty}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : errorMessage && !detail ? (
                        <Typography color="error">{errorMessage}</Typography>
                    ) : properties.length === 0 ? (
                        <Typography color="textSecondary">
                            No per-property data was stored for this snapshot.
                        </Typography>
                    ) : filteredProperties.length === 0 ? (
                        <Typography color="textSecondary">
                            No properties match this filter.
                        </Typography>
                    ) : (
                        <Box className={classes.tableWrap}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Property</TableCell>
                                        <TableCell align="right">
                                            Health
                                        </TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">
                                            Vendors
                                        </TableCell>
                                        <TableCell align="right">
                                            Damage Types
                                        </TableCell>
                                        <TableCell align="right">
                                            Damages (30d)
                                        </TableCell>
                                        <TableCell>Lease Sync</TableCell>
                                        <TableCell align="right">
                                            Schedules
                                        </TableCell>
                                        <TableCell align="right">
                                            Scheduled WOs
                                        </TableCell>
                                        <TableCell align="right">
                                            Requests
                                        </TableCell>
                                        <TableCell align="right">
                                            Move-Out Insp.
                                        </TableCell>
                                        <TableCell align="right">
                                            Insp. Complete
                                        </TableCell>
                                        <TableCell align="right">
                                            Insp. Scheduled
                                        </TableCell>
                                        <TableCell align="right">
                                            Report
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredProperties.map(
                                        (row: PropertySnapshotPropertyRow) => {
                                            const healthCategory = resolvePropertyHealthCategory(
                                                row,
                                            )
                                            return (
                                                <TableRow key={row.company_id}>
                                                    <TableCell
                                                        className={
                                                            classes.propertyName
                                                        }
                                                    >
                                                        {row.name}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatMetricValue(
                                                            row.health_score,
                                                            'decimal',
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <span
                                                            className={
                                                                classes.healthSwatch
                                                            }
                                                            style={{
                                                                backgroundColor:
                                                                    healthColorByCategory[
                                                                        healthCategory
                                                                    ] ||
                                                                    '#9e9e9e',
                                                            }}
                                                        />
                                                        {healthCategory}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatMetricValue(
                                                            row.vendor_count,
                                                            'number',
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatMetricValue(
                                                            row.damage_type_count,
                                                            'number',
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatMetricValue(
                                                            row.damage_count,
                                                            'number',
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.lease_sync_date
                                                            ? row.lease_sync_date.slice(
                                                                  0,
                                                                  10,
                                                              )
                                                            : 'Never'}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatMetricValue(
                                                            row.schedule_count,
                                                            'number',
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatMetricValue(
                                                            row.scheduled_workorders,
                                                            'number',
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatMetricValue(
                                                            row.request_count,
                                                            'number',
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatMetricValue(
                                                            row.move_out_inspection_count,
                                                            'number',
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatMetricValue(
                                                            row.complete_units_count,
                                                            'number',
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatMetricValue(
                                                            row.total_units_count,
                                                            'number',
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {row.xlsx_url &&
                                                        onViewReport ? (
                                                            <Button
                                                                className={
                                                                    classes.linkButton
                                                                }
                                                                color="primary"
                                                                size="small"
                                                                onClick={() =>
                                                                    onViewReport(
                                                                        row.xlsx_url as string,
                                                                    )
                                                                }
                                                            >
                                                                View
                                                            </Button>
                                                        ) : (
                                                            '—'
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        },
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    )
}
